'use client';

import { c as _c } from "react/compiler-runtime";
import { useFindSimilarMarketsByText } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef, useState } from 'react';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { levenshtein } from '@/lib/similar-markets/levenshtein';
const DEBOUNCE_MS = 500;
const REBREACH_RATIO = 0.2;
const MIN_QUERY_LENGTH = 10;
export interface SimilarDebounceState {
  readonly status: 'idle' | 'loading' | 'matches' | 'empty' | 'error';
  readonly matches: ReadonlyArray<MarketIdentityData & {
    readonly pubkey: string;
    readonly title: string;
    readonly category: string | null;
    readonly status: number;
    readonly coverImageUrl: string;
    readonly similarity: number;
  }>;
  readonly dismissed: boolean;
  readonly dismiss: () => void;
}
export function useSimilarDebounce(queryText, threshold) {
  const $ = _c(34);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    mutate,
    data,
    isPending,
    isError,
    reset
  } = useFindSimilarMarketsByText(t0);
  const [dismissed, setDismissed] = useState(false);
  const [dismissedText, setDismissedText] = useState(null);
  const lastFiredQuery = useRef("");
  const debounceTimer = useRef(null);
  if (dismissed && dismissedText !== null) {
    const denom = Math.max(queryText.length, 1);
    if (levenshtein(dismissedText, queryText) / denom > REBREACH_RATIO) {
      setDismissed(false);
      setDismissedText(null);
    }
  }
  let t1;
  let t2;
  if ($[1] !== dismissed || $[2] !== mutate || $[3] !== queryText || $[4] !== reset) {
    t1 = () => {
      if (dismissed) {
        return;
      }
      if (queryText.length < MIN_QUERY_LENGTH) {
        reset();
        return;
      }
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        if (lastFiredQuery.current === queryText) {
          return;
        }
        lastFiredQuery.current = queryText;
        mutate({
          query: queryText
        });
      }, DEBOUNCE_MS);
      return () => {
        if (debounceTimer.current !== null) {
          clearTimeout(debounceTimer.current);
        }
      };
    };
    t2 = [queryText, dismissed, mutate, reset];
    $[1] = dismissed;
    $[2] = mutate;
    $[3] = queryText;
    $[4] = reset;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== queryText) {
    t3 = () => {
      setDismissed(true);
      setDismissedText(queryText);
    };
    $[7] = queryText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const dismiss = t3;
  if (dismissed) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = [];
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== dismiss) {
      t5 = {
        status: "idle",
        matches: t4,
        dismissed: true,
        dismiss
      };
      $[10] = dismiss;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  if (queryText.length < MIN_QUERY_LENGTH) {
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = [];
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== dismiss) {
      t5 = {
        status: "idle",
        matches: t4,
        dismissed: false,
        dismiss
      };
      $[13] = dismiss;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  if (isPending) {
    let t4;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = [];
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== dismiss) {
      t5 = {
        status: "loading",
        matches: t4,
        dismissed: false,
        dismiss
      };
      $[16] = dismiss;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  if (isError) {
    let t4;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = [];
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    let t5;
    if ($[19] !== dismiss) {
      t5 = {
        status: "error",
        matches: t4,
        dismissed: false,
        dismiss
      };
      $[19] = dismiss;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  if (data === undefined) {
    let t4;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = [];
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    let t5;
    if ($[22] !== dismiss) {
      t5 = {
        status: "loading",
        matches: t4,
        dismissed: false,
        dismiss
      };
      $[22] = dismiss;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    return t5;
  }
  let t4;
  let t5;
  if ($[24] !== data || $[25] !== threshold) {
    const rows = data as ReadonlyArray<MarketIdentityData & {
      pubkey: string;
      title: string;
      category: string | null;
      status: number;
      similarity: number;
      coverImageUrl: string;
    }> | undefined ?? [];
    let t6;
    if ($[28] !== threshold) {
      t6 = r => r.similarity >= threshold;
      $[28] = threshold;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    const matches = rows.filter(t6);
    t4 = matches.length > 0 ? "matches" : "empty";
    t5 = matches.map(_temp);
    $[24] = data;
    $[25] = threshold;
    $[26] = t4;
    $[27] = t5;
  } else {
    t4 = $[26];
    t5 = $[27];
  }
  let t6;
  if ($[30] !== dismiss || $[31] !== t4 || $[32] !== t5) {
    t6 = {
      status: t4,
      matches: t5,
      dismissed: false,
      dismiss
    };
    $[30] = dismiss;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  return t6;
}
function _temp(r_0) {
  return {
    pubkey: r_0.pubkey,
    marketType: r_0.marketType,
    partnerCreatorId: r_0.partnerCreatorId,
    associatedCreator: r_0.associatedCreator,
    title: r_0.title,
    category: r_0.category,
    status: r_0.status,
    coverImageUrl: r_0.coverImageUrl,
    similarity: r_0.similarity
  };
}